// ═══════════════════════════════════════════════════════════════════════════
// Panel de pagos de una remisión — lote 2.5
//
// Sustituye la cadena de prompt() del primer intento, que tenía dos defectos
// señalados por el operador: no dejaba ver los pagos ya registrados y, una vez
// saldada la remisión, no había forma de corregir un error de dedo.
//
// Aquí los pagos son visibles y REVERSIBLES: revertir registra el movimiento
// opuesto y conserva ambos. Nada se borra — es la misma regla append-only del
// inventario, y lo que permite auditar después.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState } from "react";
import { fmt } from "../pdf/comunes.js";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";
import { totalPagado, saldoPendiente, estadoCobranza, registrarPago,
         revertirPago, estaRevertido, requiereREP, pagosSinREP, esPPD, saldoAFavor,
         complementoVivoDe } from "../dominio/cobranza.js";
import { cuentasActivas } from "../datos/empresa.js";
import { emitirComplemento, verCfdiConXml, cancelarEnServidor } from "../fiscal/timbrado-servidor.js";
import { aplicarSaldoAFavor } from "../datos/remisiones-pg.js";
import { programarSincronizacion } from "../datos/sincronizador.js";

const FORMAS = ["Transferencia", "Efectivo", "Cheque", "Tarjeta", "Depósito"];

/** AAAA-MM-DD en la zona del aparato, que es lo que espera un <input type="date">. */
function fechaLocal(d) {
  const dos = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

export function PagosPanel({ inv, onGuardar, onCerrar, onIrAConfig, tenantId }) {
  const cuentas = cuentasActivas();
  // ── El monto se PROPONE, no se teclea ────────────────────────────────────
  //
  // Arranca en lo que falta por cobrar, que es lo que se registra el 90% de las
  // veces. El operador: "para registrar un pago el monto total debería ser por
  // defecto; si es cero implica teclear la cantidad."
  //
  // Y se vuelve a proponer DESPUÉS de cada registro: antes se vaciaba y quedaba
  // en blanco, así que capturar dos abonos seguidos obligaba a teclear el
  // segundo a mano — justo cuando ya se sabe cuánto falta.
  const [monto, setMonto] = useState(() => {
    const s = saldoPendiente(inv);
    return s > 0 ? s.toFixed(2) : "";
  });
  const [metodo, setMetodo] = useState("Transferencia");
  const [cuenta, setCuenta] = useState(cuentas[0]?.etiqueta || "");
  // La fecha por omisión es HOY. Antes era la de emisión de la remisión, y eso
  // tenía un costo fiscal: todos los pagos capturados sin tocar la fecha
  // quedaban en el MISMO instante, y el complemento de pago no sabía cuál iba
  // primero. Así salió un REP de Novo Wine que declaraba un adeudo de 40,000
  // sobre una factura liquidada (ver la migración 0063). Además, la fecha del
  // pago va en el REP: dejar la de emisión es declararle al SAT un día en que
  // no se cobró. Si el pago fue otro día, se cambia a mano.
  //
  // En hora LOCAL, no con toISOString: de noche en México la fecha UTC ya es
  // mañana.
  const [fecha, setFecha] = useState(() => fechaLocal(new Date()));
  const fechaEmision = inv.date ? fechaLocal(new Date(inv.date)) : null;
  const [nota, setNota] = useState("");
  // Cortesía: el vino salió y no se espera pago. Es un modo del panel y no una
  // pantalla aparte porque cierra el mismo saldo que un pago — solo que sin
  // dinero de por medio.
  const [kind, setKind] = useState("dinero");
  const [motivo, setMotivo] = useState("");
  // El complemento de pago. Se detectaba desde hace tiempo y no había con qué
  // emitirlo: se veía el aviso y no se podía hacer nada con él.
  const [repDe, setRepDe] = useState(null);      // id del pago en curso
  const [aplicando, setAplicando] = useState(false);
  const [repError, setRepError] = useState("");
  const [error, setError] = useState("");

  const pagos = inv.payments || [];
  const saldo = saldoPendiente(inv);
  const aFavor = saldoAFavor(inv);
  // Los que deben complemento, según el dominio. Un REP CANCELADO no cuenta
  // como emitido: ante el SAT es como si nunca se hubiera declarado, así que
  // ese pago vuelve a aparecer aquí y se puede reemitir.
  const sinREP = new Set(pagosSinREP(inv).map(p => p.id));
  const repVivo = (p) => !!p.rep?.facturapiId && p.rep.status !== "cancelado";
  // El de la CUENTA del cliente, que es el que se puede gastar. El de la
  // remisión solo dice dónde ocurrió el excedente.
  const favorCliente = Number(inv.client?.aFavor ?? aFavor) || 0;
  const estado = estadoCobranza(inv);

  // ¿El usuario escribió algo distinto de lo propuesto? Distinguirlo permite
  // apagar el botón cuando no hay nada que cobrar sin estorbar a quien SÍ
  // quiere registrar algo a propósito.
  const montoNum = parseFloat(String(monto).replace(/[^\d.-]/g, ""));
  const montoTecleado = !Number.isNaN(montoNum) && montoNum !== 0;
  const hayAlgoQueRegistrar = montoTecleado &&
    (kind === "dinero" || String(motivo || "").trim().length > 0);

  const agregar = () => {
    const m = parseFloat(String(monto).replace(/[^\d.-]/g, ""));
    if (!m) { setError("Escribe un monto distinto de cero."); return; }
    try {
      onGuardar(registrarPago(inv, {
        monto: m, metodo: kind === "dinero" ? metodo : "", cuenta: kind === "dinero" ? cuenta : "",
        fecha: new Date(fecha + "T12:00:00").toISOString(),
        nota, kind, motivo,
      }));
      // Se propone lo que queda tras este movimiento, no se vacía.
      const quedaba = saldoPendiente(inv) - m;
      setMonto(quedaba > 0.004 ? quedaba.toFixed(2) : "");
      setNota(""); setMotivo(""); setError("");
    } catch (e) { setError(e.message); }
  };

  // ── Revertir, y antes cancelar el complemento si lo hay ──────────────────
  //
  // Decisión del operador: al revertir un pago con REP emitido se cancela el
  // COMPLEMENTO y la factura sigue viva. La diferencia con cancelar una factura
  // es de dónde parte el hecho: allá muere la factura y arrastra lo que cuelga
  // de ella; aquí lo que no ocurrió es el pago —el cheque se devolvió— pero la
  // venta sí ocurrió y el vino está en la bodega del cliente. La factura vuelve
  // a estar por cobrar.
  //
  // El orden importa igual que allá: primero el REP, y si su cancelación falla
  // NO se revierte. Un pago revertido con su complemento vivo declara ante el
  // SAT dinero que ya no existe.
  const revertir = async (pagoId) => {
    const rep = complementoVivoDe(inv, pagoId);
    const aviso = rep
      ? `¿Revertir este pago?\n\n` +
        `Tiene un COMPLEMENTO DE PAGO vigente ante el SAT. Se cancelará primero ` +
        `el complemento y después se registrará la reversa.\n\n` +
        `La factura NO se cancela: la venta ocurrió y sigue por cobrar.`
      : "¿Revertir este pago?\n\nNo se borra: se registra el movimiento contrario y quedan los dos, para que la corrección sea auditable.";
    if (!confirm(aviso)) return;

    let base = inv;
    if (rep) {
      setRepDe(pagoId); setRepError("");
      try {
        await cancelarEnServidor(rep.facturapiId, "02", tenantId);
      } catch (e) {
        setRepDe(null);
        setRepError(`No se pudo cancelar el complemento: ${e.message}. ` +
                    `El pago NO se revirtió.`);
        return;
      }
      setRepDe(null);
      // El REP muerto viaja en el mismo objeto que la reversa: si se guardaran
      // por separado, un fallo entre los dos dejaría el pago revertido con su
      // complemento marcado como vigente.
      base = { ...inv, payments: (inv.payments || []).map(p =>
        p.id === pagoId && p.rep ? { ...p, rep: { ...p.rep, status: "cancelado" } } : p) };
    }
    try { onGuardar(revertirPago(base, pagoId)); setError(""); }
    catch (e) { setError(e.message); }
  };

  // ── Cancelar el complemento y reemitirlo, SIN tocar el pago ─────────────
  //
  // El operador: "En ninguna de las vistas es claro cómo cancelar el
  // complemento de pago para reemitirlo. La única opción ahora en esa pestaña
  // es revertir el pago, lo cual no es correcto."
  //
  // No lo es: revertir dice que el dinero NO entró, y aquí sí entró. Lo que
  // está mal es el comprobante —el de Novo Wine declaraba un saldo que no era,
  // por el defecto que arregló la 0063—. Así que muere el REP y el pago queda
  // como estaba, pidiendo uno nuevo.
  //
  // Motivo 02, "emitido con errores sin relación": el mismo que usa revertir.
  // El 01 exige emitir antes el sustituto y relacionarlo, y el servidor no
  // deja emitir un segundo REP mientras el primero siga vigente.
  //
  // Son dos pasos y no uno a propósito: si la reemisión falla, el pago queda
  // con su botón «Emitir REP» y se reintenta, en vez de un estado a medias que
  // no se ve.
  const cancelarREP = async (p) => {
    if (!confirm(
      `¿Cancelar el complemento de este pago de ${fmt(p.monto)}?\n\n` +
      `UUID ${p.rep.uuid}\n\n` +
      `Se cancela ante el SAT como emitido con errores. El pago NO se toca: ` +
      `sigue registrado y vuelve a pedir su complemento, que podrás emitir ` +
      `enseguida con las cifras correctas.`)) return;

    setRepDe(p.id + "cancelar"); setRepError("");
    try {
      await cancelarEnServidor(p.rep.facturapiId, "02", tenantId);
    } catch (e) {
      setRepDe(null);
      setRepError(`No se pudo cancelar el complemento: ${e.message}`);
      return;
    }
    const sinVivo = { ...inv, payments: (inv.payments || []).map(x =>
      x.id === p.id ? { ...x, rep: { ...x.rep, status: "cancelado" } } : x) };
    onGuardar(sinVivo);
    setRepDe(null);

    if (!confirm("Complemento cancelado.\n\n¿Emitir ahora el nuevo?")) return;
    setRepDe(p.id);
    try {
      const r = await emitirComplemento(tenantId, p.id);
      onGuardar({ ...sinVivo, payments: sinVivo.payments.map(x =>
        x.id === p.id
          ? { ...x, rep: { facturapiId: r.id, uuid: r.uuid,
                           status: "vigente", url: r.verification_url } }
          : x) });
    } catch (e) {
      setRepError(`El complemento viejo quedó cancelado, pero el nuevo no se emitió: ` +
                  `${e.message}. Reintenta con «Emitir REP».`);
    }
    setRepDe(null);
  };

  return (
    <div onClick={onCerrar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#0d0d0d", border: "1px solid #222",
        borderRadius: 10, padding: 20, maxWidth: 560, width: "100%", maxHeight: "88vh", overflowY: "auto" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <h3 style={{ margin: 0, color: "#e8e0d4", fontSize: 16 }}>Pagos · Remisión #{inv.folio}</h3>
          <button onClick={onCerrar} style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: 20 }}>×</button>
        </div>
        <div style={{ color: "#888", fontSize: 12, marginBottom: 14 }}>{inv.client?.name}</div>

        <div style={{ display: "flex", gap: 18, padding: "10px 12px", background: "#0a0a0a",
          borderRadius: 6, marginBottom: 16, fontSize: 12, flexWrap: "wrap" }}>
          <span><span style={{ color: "#666" }}>Total </span>{fmt(inv.total)}</span>
          <span><span style={{ color: "#666" }}>Pagado </span>{fmt(totalPagado(inv))}</span>
          <span><span style={{ color: "#666" }}>Saldo </span>
            <b style={{ color: saldo > 0 ? "#c9a96e" : "#4ade80" }}>{fmt(saldo)}</b></span>
          <span style={{ marginLeft: "auto", color: estado.color }}>{estado.etiqueta}</span>
        </div>

        {/* ── Saldo a favor, donde no se pueda pasar por alto ────────────────
            El operador: "puede ser que el cliente haya duplicado el pago y
            debemos tener evidencia y poder hacer conciliación."
            Antes el excedente desaparecía —`saldoPendiente` recortaba a cero—
            así que una remisión pagada dos veces se veía igual que una pagada
            una. Ahora se declara, con el monto y con qué hacer. */}
        {/* ── El saldo a favor es del CLIENTE, no de este documento ─────────
            Corrección del operador: "el saldo a favor no debe estar asociado a
            una remisión... si hay saldo a favor se puede aplicar para registrar
            pagos parciales o totales de otra remisión o factura."
            Por eso el número que se enseña es el de su cuenta corriente —que
            puede venir de otra venta— y por eso hay un botón para gastarlo
            aquí. */}
        {favorCliente > 0 && (
          <div style={{ marginTop: 10, marginBottom: 14, padding: "12px 14px",
                        background: "#0d1a1a", border: "1px solid #1a3a3a",
                        borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10,
                          flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 1 }}>
                {inv.client?.name?.toUpperCase()} TIENE A FAVOR
              </span>
              <span style={{ fontSize: 20, fontWeight: 600, color: "#70c9c9" }}>
                {fmt(favorCliente)}
              </span>
              {saldo > 0 && (
                <button onClick={async () => {
                    setAplicando(true); setError("");
                    try {
                      const r = await aplicarSaldoAFavor(tenantId, inv.id);
                      // ── Si la factura es PPD, esto obliga a un complemento ──
                      //
                      // El operador: "si una factura PPD se salda aplicando saldo
                      // a favor se debe emitir un complemento de pago. Se ofrece
                      // emitirlo... Nada de esto es automático, se debe
                      // mencionar."
                      //
                      // Por eso se DICE aquí y el panel NO se cierra: cerrarlo
                      // escondía el pago recién creado y con él el botón de
                      // emitir. Emitirlo solo tampoco: un CFDI ante el SAT no se
                      // manda sin que alguien lo decida.
                      const pediraREP = esPPD(inv) && (inv.cfdiId || inv.cfdiUuid);
                      alert(`Se aplicaron ${fmt(r.aplicado)} de su saldo.\n\n` +
                            `Le quedan ${fmt(r.queda_a_favor)} a favor.\n` +
                            `A esta remisión le faltan ${fmt(r.falta_en_remision)}.` +
                            (pediraREP
                              ? `\n\n🧾 Esta factura es PPD: la aplicación cuenta como ` +
                                `pago recibido y hay que emitir su complemento. ` +
                                `Abajo, junto al movimiento, está el botón «Emitir REP».`
                              : ""));
                      programarSincronizacion();
                    } catch (e) { setError(e.message); }
                    setAplicando(false);
                  }}
                  disabled={aplicando}
                  style={{ marginLeft: "auto", ...btnPrimary, padding: "7px 14px",
                           fontSize: 12,
                           background: "linear-gradient(135deg, #143a3a, #0d2a2a)",
                           borderColor: "#2a5a5a", color: "#70c9c9",
                           opacity: aplicando ? 0.5 : 1 }}>
                  {aplicando ? "Aplicando…" : `Aplicar a esta remisión`}
                </button>
              )}
            </div>
            {/* Este texto decía "o aplicándolo a otra remisión" y NO HAY CÓMO:
                el operador lo cazó. Es el mismo error que la zona de peligro,
                que prometía "para deshacer: Importar ese archivo" sin que
                existiera botón de importar.
                Una pantalla que ofrece algo que no puede hacer es peor que una
                que calla: manda a buscar durante minutos algo que no está. Así
                que aquí solo se nombra lo que SÍ se puede hoy. */}
            <div style={{ fontSize: 11, color: "#7a9a9a", lineHeight: 1.7, marginTop: 6 }}>
              Es dinero suyo que todavía no ampara ningún documento — puede venir
              de esta venta o de otra. {saldo > 0
                ? "Se puede aplicar aquí para cubrir lo que falta."
                : "Esta remisión ya está cubierta; aplícalo desde otra que deba."}
              <br />
              Si sobró por error, <b>revierte</b> el movimiento que lo causó con su
              botón: los de abajo son la evidencia.
            </div>
          </div>
        )}

        {/* El aviso dice lo que FALTA, y desaparece cuando no falta nada.
            Antes seguía encendido después de emitir —"el sistema aún no lo
            emite", sobre algo recién emitido— y un aviso que no se apaga deja
            de leerse. */}
        {(() => {
          const faltan = pagosSinREP(inv);
          if (!faltan.length) {
            if (!esPPD(inv) || !pagos.some(p => p.rep)) return null;
            return (
              <div style={{ padding: "8px 12px", background: "#0d1f0d", borderRadius: 6,
                            marginBottom: 14, fontSize: 11, color: "#a8d060" }}>
                ✓ Todos los pagos tienen su complemento ante el SAT.
              </div>
            );
          }
          return (
            <div style={{ padding: "8px 12px", background: "#1f1a0d", borderRadius: 6,
                          marginBottom: 14, fontSize: 11, color: "#c9a96e" }}>
              ⚠️ Factura PPD: {faltan.length === 1 ? "un pago" : `${faltan.length} pagos`}
              {" "}sin <b>complemento (REP)</b> ante el SAT.
              {" "}Emítelo con el botón de cada renglón, aquí abajo.
            </div>
          );
        })()}

        {/* ── Movimientos registrados ── */}
        {pagos.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, color: "#70c9c9", letterSpacing: 2, marginBottom: 8 }}>MOVIMIENTOS</div>
            {pagos.map(p => {
              const revertido = estaRevertido(inv, p.id);
              const esReversa = !!p.revierteA;
              return (
                <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
                  borderBottom: "1px solid #161616", fontSize: 12,
                  opacity: revertido ? 0.45 : 1 }}>
                  <span style={{ color: "#666", minWidth: 74 }}>
                    {new Date(p.fecha).toLocaleDateString("es-MX")}
                  </span>
                  <span style={{ color: p.monto < 0 ? "#c97070" : "#4ade80", minWidth: 90,
                    textDecoration: revertido ? "line-through" : "none" }}>
                    {fmt(p.monto)}
                  </span>
                  <span style={{ color: "#888", flex: 1 }}>
                    {p.kind && p.kind !== "dinero"
                      ? <span style={{ color: "#a0a0c9" }}>
                          {p.kind === "cortesia" ? "Cortesía" : "Demostración"}
                          {p.motivo ? ` · ${p.motivo}` : ""}
                        </span>
                      : [p.metodo, p.cuenta].filter(Boolean).join(" · ")}
                    {p.nota ? <span style={{ color: "#555" }}> — {p.nota}</span> : null}
                  </span>

                  {/* El complemento, DONDE hace falta: junto al pago que lo
                      obliga. En una pantalla aparte habría que casar folios a
                      ojo, que es lo que veníamos quitando.
                      Solo en pagos de dinero: una cortesía no se le declara al
                      SAT como pago recibido, porque no entró dinero. */}
                  {/* El complemento: emitirlo, y —lo que faltaba— VERLO y
                      poder bajarlo. Se emitía y quedaba en la base sin que la
                      pantalla se enterara: el operador lo reportó así, "dice
                      que se genera REP pero no hay dónde verlo". */}
                  {/* QUIÉN debe complemento lo decide `pagosSinREP`, no un
                      filtro repetido aquí. Estaba escrito como
                      `p.kind === "dinero"` y eso dejaba fuera las APLICACIONES
                      de saldo a favor: una factura PPD saldada con crédito del
                      cliente no habría tenido dónde emitir su REP, que es justo
                      lo que el operador pidió que se ofreciera.
                      Con el Set, la pantalla y la regla no pueden divergir. */}
                  {(sinREP.has(p.id) || repVivo(p))
                    && !esReversa && !revertido && p.monto > 0 && (
                    repVivo(p) ? (
                      <span style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                        <span title={`Complemento ${p.rep.uuid}`}
                          style={{ fontSize: 9, color: "#4ade80", letterSpacing: 1 }}>
                          REP ✓
                        </span>
                        {/* Un botón, los dos archivos. Abre el PDF en el
                            visor con el XML a bordo, y desde ahí se comparten
                            o se descargan juntos con un toque propio —el que
                            Safari exige para abrir la hoja—. */}
                        <button
                          onClick={async () => {
                            setRepDe(p.id + "docs"); setRepError("");
                            try {
                              await verCfdiConXml(p.rep.facturapiId, tenantId,
                                `Complemento de pago · Remisión #${inv.folio}`);
                            } catch (e) { setRepError(e.message); }
                            setRepDe(null);
                          }}
                          disabled={repDe === p.id + "docs"}
                          title="Ver el complemento y compartir o descargar su PDF y su XML"
                          style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3,
                                   cursor: "pointer", fontFamily: "inherit",
                                   background: "transparent", border: "1px solid #1a3a1a",
                                   color: "#4ade80" }}>
                          {repDe === p.id + "docs" ? "…" : "PDF+XML"}
                        </button>
                        {p.rep.url && (
                          <a href={p.rep.url} target="_blank" rel="noreferrer"
                            title="Verificarlo en el portal del SAT"
                            style={{ fontSize: 9, color: "#70c9c9", textDecoration: "none",
                                     border: "1px solid #1a3a3a", borderRadius: 3,
                                     padding: "1px 6px" }}>
                            SAT
                          </a>
                        )}
                        <button onClick={() => cancelarREP(p)}
                          disabled={!!repDe}
                          title="Cancelar este complemento ante el SAT para emitirlo de nuevo. El pago no se toca."
                          style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3,
                                   cursor: "pointer", fontFamily: "inherit",
                                   background: "transparent", border: "1px solid #3a2a1a",
                                   color: "#e0a060" }}>
                          {repDe === p.id + "cancelar" ? "…" : "Cancelar REP"}
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={async () => {
                          setRepDe(p.id); setRepError("");
                          try {
                            const r = await emitirComplemento(tenantId, p.id);
                            onGuardar({ ...inv, payments: (inv.payments || []).map(
                              x => x.id === p.id
                                ? { ...x, rep: { facturapiId: r.id, uuid: r.uuid,
                                                 status: "vigente", url: r.verification_url } }
                                : x) });
                          } catch (e) { setRepError(e.message); }
                          setRepDe(null);
                        }}
                        disabled={repDe === p.id}
                        title="El SAT lo exige por cada pago de una factura PPD"
                        style={{ fontSize: 9, padding: "2px 7px", borderRadius: 4,
                                 cursor: "pointer", fontFamily: "inherit",
                                 background: "#1f1a0d", border: "1px solid #3a331a",
                                 color: "#facc15" }}>
                        {repDe === p.id ? "…" : (p.rep?.status === "cancelado" ? "Reemitir REP" : "Emitir REP")}
                      </button>
                    )
                  )}
                  {!esReversa && !revertido && (
                    <button onClick={() => revertir(p.id)} title="Revertir este pago"
                      style={{ background: "none", border: "1px solid #3a1a1a", borderRadius: 4,
                        color: "#c97070", cursor: "pointer", padding: "2px 8px", fontSize: 10 }}>
                      ↩ Revertir
                    </button>
                  )}
                  {revertido && <span style={{ color: "#666", fontSize: 10 }}>revertido</span>}
                  {esReversa && <span style={{ color: "#666", fontSize: 10 }}>corrección</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Registrar uno nuevo ── */}
        <div style={{ fontSize: 10, color: "#70c9c9", letterSpacing: 2, marginBottom: 8 }}>REGISTRAR PAGO</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ fontSize: 10, color: "#666" }}>Monto</label>
            <input value={monto} onChange={e => setMonto(e.target.value)} inputMode="decimal"
              placeholder="0.00" style={{ ...inputStyle, width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: "#666" }}>Fecha</label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
              style={{ ...inputStyle, width: "100%" }} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: "#666" }}>Forma de pago</label>
            <select value={metodo} onChange={e => setMetodo(e.target.value)} style={{ ...selectStyle, width: "100%" }}>
              {FORMAS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, color: "#666" }}>Cuenta que recibe</label>
            {cuentas.length > 0 ? (
              <select value={cuenta} onChange={e => setCuenta(e.target.value)} style={{ ...selectStyle, width: "100%" }}>
                {cuentas.map(c => <option key={c.id} value={c.etiqueta}>{c.etiqueta}</option>)}
              </select>
            ) : (
              <div style={{ padding: "8px 10px", background: "#1f1a0d", borderRadius: 6, fontSize: 11, color: "#c9a96e" }}>
                No hay cuentas capturadas.{" "}
                {onIrAConfig && (
                  <button onClick={() => { onCerrar(); onIrAConfig(); }}
                    style={{ background: "none", border: "none", color: "#c9a96e", textDecoration: "underline",
                      cursor: "pointer", padding: 0, font: "inherit" }}>
                    Captúralas en Config → Datos de la vinícola
                  </button>
                )}
              </div>
            )}
          </div>
          {kind !== "dinero" && (
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 10, color: "#666" }}>
                Por qué no se cobra <span style={{ color: "#c9a96e" }}>·  obligatorio</span>
              </label>
              <input value={motivo} onChange={e => setMotivo(e.target.value)}
                placeholder="Ej. cata en centro de consumo del distribuidor"
                style={{ ...inputStyle, width: "100%" }} />
              <div style={{ fontSize: 10, color: "#556", marginTop: 4, lineHeight: 1.6 }}>
                Se guarda con el movimiento. Sin esto, en tres meses nadie va a poder
                explicar por qué esa remisión no se cobró.
              </div>
            </div>
          )}
          <div style={{ gridColumn: "1/-1" }}>
            <label style={{ fontSize: 10, color: "#666" }}>Nota (opcional)</label>
            <input value={nota} onChange={e => setNota(e.target.value)}
              placeholder="referencia, folio de transferencia…" style={{ ...inputStyle, width: "100%" }} />
          </div>
        </div>

        {fechaEmision && fecha < fechaEmision && (
          <div style={{ color: "#c9a96e", fontSize: 11, marginTop: 10 }}>
            ⚠️ La fecha del pago es anterior a la emisión de la remisión ({new Date(inv.date).toLocaleDateString("es-MX")}).
            Aquí no se manejan anticipos: verifica.
          </div>
        )}
        {error && <div style={{ color: "#c97070", fontSize: 12, marginTop: 10 }}>⚠️ {error}</div>}
        {repError && (
          <div style={{ color: "#c97070", fontSize: 12, marginTop: 10 }}>
            ⚠️ No se pudo emitir el complemento: {repError}
          </div>
        )}

        {/* Cortesía y demostración cierran el saldo SIN ser cobro. El operador:
            "hay remisiones que nunca se van a pagar... registrar un pago debería
            tener la opción de poner cortesía, así sabemos que el vino salió,
            pero no esperamos pago." */}
        <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }}>
          {[["dinero", "Entró dinero"], ["cortesia", "Cortesía"],
            ["demostracion", "Demostración"]].map(([k, t]) => (
            <button key={k} onClick={() => { setKind(k); setError(""); }}
              style={{ fontSize: 11, padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                       fontFamily: "inherit",
                       background: kind === k ? "#1a1810" : "transparent",
                       border: `1px solid ${kind === k ? "#3a3222" : "#222"}`,
                       color: kind === k ? "#c9a96e" : "#666" }}>
              {t}
            </button>
          ))}
        </div>

        {/* ── Cuando ya no hay saldo, el botón lo dice ─────────────────────
            El operador: "si se liquida y lo adeudado queda en cero, el botón
            sigue diciendo registrar pago; debería cambiar de color o no poder
            seleccionarse."
            Se apaga, pero NO se esconde ni se bloquea del todo: si alguien
            teclea un monto a propósito, se deja —hay casos reales, como
            registrar un cobro duplicado que después se revierte—. Lo que se
            quita es el ofrecimiento, no la posibilidad. */}
        {saldo <= 0 && aFavor <= 0 && !montoTecleado && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: "#0d1f0d",
                        border: "1px solid #1a3a1a", borderRadius: 6,
                        fontSize: 11, color: "#a8d060" }}>
            ✓ Esta remisión ya no tiene saldo. Si aun así necesitas registrar un
            movimiento, escribe el monto arriba.
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={agregar} disabled={!hayAlgoQueRegistrar}
            style={{ ...btnPrimary,
                     ...(hayAlgoQueRegistrar ? {} : {
                       background: "transparent", border: "1px solid #222",
                       color: "#555", cursor: "not-allowed" }) }}>
            {kind === "dinero"
              ? (saldo > 0 && !montoTecleado ? `Registrar ${fmt(saldo)}` : "Registrar pago")
              : "Registrar sin cobro"}
          </button>
          <button onClick={onCerrar} style={btnSecondary}>Cerrar</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 10, color: "#555" }}>
          Los pagos no se editan ni se borran: para corregir se revierte y se registra el correcto.
          Así queda el rastro de qué pasó.
        </div>
      </div>
    </div>
  );
}
